import { Module, Commit, ActionTree, MutationTree, Dispatch } from 'vuex';

import type { ListElements } from '@/typings/customilyElements/layer';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import type { CanvasElement } from '@/typings/customilyElements/canvasElements';
import { CanvasEx } from '@/typings/canvas';

import { updateElementOrder, updateEpsZIndex } from '@/utils/elementsUtils';

import { updateItemElement, findTreeItemByIds } from './utils/elementUtils';
import { getFlatChildrenByItem } from '@/utils/layers/groupUtils';
import {
    handleElementsSelection,
    handleElementsSelectionWithCtrlKey,
    handleElementsSelectionWithShiftKey
} from './utils/selectionUtils';
import type { LayersTreeState } from './types';

const defaultState: LayersTreeState = {
    treeItems: [],
    selectedItems: [],
    rawElements: {
        canvasElements: [],
        groupElements: [],
        pageEpsElements: []
    },
    settings: {
        events: undefined
    },
    flags: undefined,
    canvas: undefined,
    template: undefined,
    currentDraggedItem: undefined,
    currentTargetItem: undefined,
    isUpdatingTree: false
};

const getters = {
    areAllElementsSelected: (
        state: LayersTreeState,
        getters: {
            getAllSelectedCanvasElements: ListElements[];
        }
    ) => {
        return getters.getAllSelectedCanvasElements?.length === state.rawElements.canvasElements?.length;
    },
    areAllElementsUnSelected: (state: LayersTreeState) => {
        return state.selectedItems?.length === 0;
    },
    areAllSelectedElementsLocked: (_: LayersTreeState, getters: { getAllSelectedCanvasElements: CanvasElement[] }) => {
        return getters.getAllSelectedCanvasElements?.every((element) => element.locked);
    },
    areAllSelectedElementsUnlocked: (
        _: LayersTreeState,
        getters: { getAllSelectedCanvasElements: CanvasElement[] }
    ) => {
        return getters.getAllSelectedCanvasElements.every((element) => !element.locked);
    },
    areAllSelectedElementsVisible: (_: LayersTreeState, getters: { getAllSelectedCanvasElements: CanvasElement[] }) => {
        return getters.getAllSelectedCanvasElements.every((element) => element.visible);
    },
    areAllSelectedElementsHidden: (_: LayersTreeState, getters: { getAllSelectedCanvasElements: CanvasElement[] }) => {
        return getters.getAllSelectedCanvasElements.every((element) => !element.visible);
    },
    getAllSelectedCanvasElements: (state: LayersTreeState) => {
        return state.selectedItems
            ?.filter((item) => item.type === ListElementType.CanvasElement)
            .map((item) => item.element);
    },
    getIsActiveEps: (state: LayersTreeState) => (id: number) => {
        const firstSelectedElement = state.selectedItems?.[0];
        return firstSelectedElement ? id === firstSelectedElement.element.pageEpsId : false;
    },
    getIsActive: (state: LayersTreeState) => (item: ListElements) => {
        const parentElements = state.selectedItems?.filter((element) =>
            [ListElementType.PageEps, ListElementType.Group].includes(element.type)
        );
        if (!parentElements || parentElements.length === 0) {
            return false;
        }

        const children = parentElements.flatMap((element) => getFlatChildrenByItem(element));
        return children.some((child) => child.element.uuid === item.element.uuid);
    },
    getChildrenFromSelectedGroup: (state: LayersTreeState) => {
        const groups = state.selectedItems?.filter((element) => element.type === ListElementType.Group);
        if (!groups || groups.length === 0 || groups.length > 1) {
            return [];
        }

        const firstGroup = groups[0];

        const children = getFlatChildrenByItem(firstGroup, false).map((child) => child.element);

        return children;
    },
    flattenTreeItems: (state: LayersTreeState) => {
        return state.treeItems.flatMap((item) => (item.children ? getFlatChildrenByItem(item) : item));
    }
};

const mutations: MutationTree<LayersTreeState> = {
    setSettings: (state, payload) => {
        state.settings = payload;
    },
    setFlags: (state, payload) => {
        state.flags = payload;
    },
    setCanvas: (state, payload) => {
        state.canvas = payload;
    },
    setTemplate: (state, payload) => {
        state.template = payload;
    },
    setRawElements: (state, payload) => {
        state.rawElements = payload;
    },
    setTreeItems: (state, payload: ListElements[]) => {
        updateItemElement(payload);
        if (state.canvas) {
            if (!state.flags?.readonly) {
                updateEpsZIndex(payload);
            }
            updateElementOrder(payload, state.canvas, !state.flags?.readonly);
        }
        state.treeItems = payload;
    },
    setEvents: (state, payload) => {
        state.settings.events = payload;
    },
    clearEvents: (state) => {
        state.settings.events = undefined;
    },
    setSelectedItems(
        state,
        payload: {
            selectedItems: ListElements[];
            canvas?: CanvasEx;
            preventStateMutation?: boolean;
        }
    ) {
        const { selectedItems, canvas, preventStateMutation } = payload;
        if (canvas) {
            canvas._selectedItems = selectedItems;
        }

        if (preventStateMutation) {
            return;
        }

        state.selectedItems = selectedItems;
        if (state.canvas) {
            state.canvas._selectedItems = selectedItems;
        }
    },
    clearSelectedItems: (
        state,
        payload: {
            canvas?: CanvasEx;
            preventStateMutation?: boolean;
        }
    ) => {
        const { canvas, preventStateMutation } = payload;
        if (canvas) {
            canvas._selectedItems = [];
        }

        if (preventStateMutation) {
            return;
        }

        state.selectedItems = [];
        if (state.canvas) {
            state.canvas._selectedItems = [];
        }
    },
    toggleLook: (state, payload) => {
        const element = state.rawElements.canvasElements.find((item) => item.uuid === payload.uuid);
        if (element) {
            state?.settings.events?.['toggleLock']?.({
                element
            });
        }
    },
    toggleVisibility: (state, payload) => {
        const element = state.rawElements.canvasElements.find((item) => item.uuid === payload.uuid);
        if (element) {
            state.settings?.events?.['toggleVisibility']?.({
                element
            });
        }
    },
    updateElementName: (state, payload) => {
        const item = payload.item;

        if (item.element) {
            const element = item.element;
            element.name = payload.name;

            if (element.type === ListElementType.PageEps) {
                const canvas = state.canvas;
                if (canvas) {
                    canvas.fire('pageeps:deselected');
                    canvas.fire('pageeps:selected', {
                        target: element
                    });
                }
                return;
            }

            state.settings?.events?.['titleChanged']?.({
                element: element
            });
        }
    },
    setDraggedItem: (state, payload) => {
        state.currentDraggedItem = payload.currentDraggedItem;
        state.currentTargetItem = payload.currentTargetItem;
    },
    clear: (state) => {
        state.canvas = undefined;
        state.template = undefined;
        state.treeItems = [];
        state.selectedItems = [];
        state.rawElements = {
            canvasElements: [],
            groupElements: [],
            pageEpsElements: []
        };
        state.settings = {
            events: undefined
        };
        state.flags = undefined;
        state.currentDraggedItem = undefined;
        state.currentTargetItem = undefined;
    },
    setIsUpdatingTree: (state, payload) => {
        state.isUpdatingTree = payload;
    }
};

const actions: ActionTree<LayersTreeState, LayersTreeState> = {
    setSettings({ commit }: { commit: Commit }, payload) {
        commit('setSettings', payload);
    },
    setFlags({ commit }: { commit: Commit }, payload) {
        commit('setFlags', payload);
    },
    setCanvas({ commit }: { commit: Commit }, payload) {
        commit('setCanvas', payload);
    },
    setTemplate({ commit }: { commit: Commit }, payload) {
        commit('setTemplate', payload);
    },
    setRawElements({ commit }: { commit: Commit }, payload) {
        commit('setRawElements', payload);
    },
    setTreeItems({ commit }: { commit: Commit }, payload) {
        commit('setTreeItems', payload);
    },
    updateElements({ commit, state }: { commit: Commit; state: LayersTreeState }, payload: ListElements[]) {
        try {
            commit('setTreeItems', payload);

            const firstTreeItem = state.selectedItems?.[0];
            if (!firstTreeItem) {
                return;
            }
            const id = firstTreeItem.element?.uuid || firstTreeItem.element?.id.toString();

            const foundElements = findTreeItemByIds(state.treeItems, [id]);
            if (!foundElements) {
                return;
            }

            if (!state.canvas) {
                return;
            }

            handleElementsSelection(
                {
                    elements: foundElements,
                    canvas: state.canvas
                },
                commit
            );
        } catch (error) {
            console.error('An error occurred trying to update the tree items', error);
        }
    },
    setEvents({ commit }: { commit: Commit }, payload) {
        commit('setEvents', payload);
    },
    clearEvents({ commit }: { commit: Commit }) {
        commit('clearEvents');
    },
    setTreeItemsByIds(
        { commit, state, dispatch }: { commit: Commit; state: LayersTreeState; dispatch: Dispatch },
        payload: {
            ids: string[];
            preventBroadcast?: boolean;
            canvas?: CanvasEx;
            fromStepOne?: boolean;
        }
    ) {
        try {
            const { ids, preventBroadcast, canvas, fromStepOne } = payload;
            const currentCanvas = canvas || state.canvas;

            if (state.settings?.events?.broadcastEvent && !preventBroadcast) {
                state.settings.events?.broadcastEvent?.({
                    callback: (data: { canvas: CanvasEx; fromStepOne?: boolean }) =>
                        dispatch('setTreeItemsByIds', {
                            ...payload,
                            preventBroadcast: true,
                            ...data
                        })
                });
                return;
            }

            const foundElements = findTreeItemByIds(state.treeItems, ids);

            if (!foundElements) {
                commit('setSelectedItems', {
                    selectedItems: [],
                    canvas: currentCanvas,
                    preventStateMutation: fromStepOne
                });
                return;
            }

            commit('setSelectedItems', {
                selectedItems: foundElements,
                canvas: currentCanvas,
                preventStateMutation: fromStepOne
            });
        } catch (error) {
            console.error('An error occurred trying to set the tree items by ids', error);
        }
    },
    setSelectedElementByIds(
        { commit, state, dispatch }: { commit: Commit; state: LayersTreeState; dispatch: Dispatch },
        payload: {
            ids: string[];
            preventBroadcast?: boolean;
            event?: PointerEvent;
            canvas?: CanvasEx;
            fromStepOne?: boolean;
        }
    ) {
        try {
            const { ids, preventBroadcast, event, canvas, fromStepOne } = payload;
            const currentCanvas = canvas || state.canvas;

            if (!currentCanvas) {
                return;
            }

            if (state.settings?.events?.broadcastEvent && !preventBroadcast) {
                state.settings.events?.broadcastEvent?.({
                    callback: (data: { canvas: CanvasEx; fromStepOne?: boolean }) =>
                        dispatch('setSelectedElementByIds', {
                            ...payload,
                            preventBroadcast: true,
                            ...data
                        })
                });
                return;
            }

            const foundElements = findTreeItemByIds(state.treeItems, ids);

            if (!foundElements) {
                if (state.settings?.events?.updateSelectedElement) {
                    state.settings?.events?.updateSelectedElement?.(null);
                }
                return;
            }

            if (state.settings?.events?.updateSelectedElement) {
                const isSingleSelection =
                    foundElements?.length === 1 &&
                    foundElements?.some((item) => item.type === ListElementType.CanvasElement);
                if (isSingleSelection) {
                    const firstItem = foundElements?.[0];
                    state.settings?.events?.updateSelectedElement?.(firstItem.element);
                }
            }

            if (event?.ctrlKey || event?.metaKey) {
                handleElementsSelectionWithCtrlKey(
                    {
                        elements: foundElements,
                        canvas: currentCanvas,
                        fromStepOne: fromStepOne
                    },
                    commit
                );
                return;
            }

            if (event?.shiftKey) {
                handleElementsSelectionWithShiftKey(
                    {
                        elements: foundElements,
                        canvas: currentCanvas,
                        fromStepOne: fromStepOne
                    },
                    state,
                    commit
                );
                return;
            }

            handleElementsSelection(
                {
                    elements: foundElements,
                    canvas: currentCanvas,
                    fromStepOne: fromStepOne
                },
                commit
            );
        } catch (error) {
            console.error('An error occurred trying to set the selected element', error);
        }
    },
    clearSelectedItems({ commit, state }: { commit: Commit; state: LayersTreeState }, canvas?: CanvasEx) {
        if (state.settings?.events?.updateSelectedElement) {
            state.settings?.events?.updateSelectedElement?.(null);
        }
        commit('clearSelectedItems', {
            canvas
        });
    },
    toggleLook({ commit }: { commit: Commit }, payload) {
        commit('toggleLook', payload);
    },
    toggleVisibility({ commit }: { commit: Commit }, payload) {
        commit('toggleVisibility', payload);
    },
    updateElementName({ commit }: { commit: Commit }, payload) {
        commit('updateElementName', payload);
    },
    selectAllUnlockedElements({ state, dispatch }: { state: LayersTreeState; dispatch: Dispatch }) {
        const unlockedElements = state.rawElements.canvasElements.filter((element) => !element.locked);
        const ids = unlockedElements.map((element) => element.uuid || element.id);
        dispatch('setSelectedElementByIds', { ids });
    },
    selectAllElements({ state, dispatch }: { state: LayersTreeState; dispatch: Dispatch }) {
        const unlockedElements = state.rawElements.canvasElements;
        const ids = unlockedElements.map((element) => element.uuid || element.id);
        dispatch('setSelectedElementByIds', { ids });
    },
    toggleAllVisible({
        getters,
        commit
    }: {
        getters: {
            getAllSelectedCanvasElements: ListElements[];
        };
        commit: Commit;
    }) {
        const selectedElements = getters.getAllSelectedCanvasElements;
        for (const element of selectedElements) {
            commit('toggleVisibility', element);
        }
    },
    toggleAllLock({
        getters,
        commit
    }: {
        getters: {
            getAllSelectedCanvasElements: ListElements[];
        };
        commit: Commit;
    }) {
        const selectedElements = getters.getAllSelectedCanvasElements;
        for (const element of selectedElements) {
            commit('toggleLook', element);
        }
    },
    setDraggedItem(
        { commit }: { commit: Commit },
        payload: { currentDraggedItem: ListElements; currentTargetItem: ListElements }
    ) {
        commit('setDraggedItem', payload);
    },
    clear({ commit }: { commit: Commit }) {
        commit('clear');
    },
    setIsUpdatingTree({ commit }: { commit: Commit }, payload: boolean) {
        commit('setIsUpdatingTree', payload);
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const store: Module<LayersTreeState, any> = {
    namespaced: true,
    state: defaultState,
    getters: getters,
    mutations: mutations,
    actions: actions
};

export default store;
